import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsApi, milestonesApi, tasksApi } from '../api/client';
import { useUIStore, useDataStore } from '../store';
import type { Project, Milestone, Task } from '../types';
import {
  Folder,
  Plus,
  Edit2,
  Trash2,
  ChevronDown,
  ChevronRight,
  Target,
  CheckCircle2,
  Clock,
  Calendar,
  MoreVertical,
  Flag,
} from 'lucide-react';

export function ProjectView() {
  const queryClient = useQueryClient();
  const { openProjectModal, setSelectedProjectId, openTaskModal, setSelectedTaskId } = useUIStore();
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  // 获取项目列表
  const { data: projectsData, isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.getAll(),
  });

  // 获取里程碑
  const { data: milestonesData } = useQuery({
    queryKey: ['milestones', selectedProject?.id],
    queryFn: () =>
      selectedProject
        ? milestonesApi.getByProject(selectedProject.id)
        : Promise.resolve({ success: true, data: [] }),
    enabled: !!selectedProject,
  });

  // 获取任务
  const { data: tasksData } = useQuery({
    queryKey: ['tasks', selectedProject?.id],
    queryFn: () =>
      selectedProject
        ? tasksApi.getAll({ projectId: selectedProject.id })
        : Promise.resolve({ success: true, data: [] }),
    enabled: !!selectedProject,
  });

  const projects = projectsData?.success ? projectsData.data : [];
  const milestones = milestonesData?.success ? milestonesData.data : [];
  const tasks = tasksData?.success ? tasksData.data : [];

  // 删除项目
  const deleteProjectMutation = useMutation({
    mutationFn: (id: string) => projectsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      if (selectedProject) {
        setSelectedProject(null);
      }
    },
  });

  // 切换项目展开
  const toggleExpand = (projectId: string) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
    }
    setExpandedProjects(newExpanded);
  };

  // 选择项目
  const handleSelectProject = (project: Project) => {
    setSelectedProject(project);
    setSelectedProjectId(project.id);
  };

  // 计算项目进度
  const getProjectProgress = (projectId: string) => {
    const projectTasks = tasks.filter((t) => t.projectId === projectId);
    if (projectTasks.length === 0) return 0;
    const completed = projectTasks.filter((t) => t.status === 'completed').length;
    return Math.round((completed / projectTasks.length) * 100);
  };

  // 获取状态图标
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-green-600" />;
      case 'in_progress':
        return <Clock className="w-4 h-4 text-blue-600" />;
      default:
        return <div className="w-4 h-4 rounded-full border-2 border-gray-300" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* 项目列表 */}
      <div className="lg:col-span-1 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">项目列表</h2>
          <button
            onClick={() => openProjectModal()}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-all"
          >
            <Plus className="w-4 h-4" />
            新建
          </button>
        </div>

        <div className="space-y-2">
          {projects.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
              <Folder className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">暂无项目</p>
              <p className="text-sm text-gray-400 mt-1">点击"新建"创建项目</p>
            </div>
          ) : (
            projects.map((project) => {
              const progress = getProjectProgress(project.id);
              const isSelected = selectedProject?.id === project.id;

              return (
                <div
                  key={project.id}
                  onClick={() => handleSelectProject(project)}
                  className={`bg-white rounded-lg shadow-sm border cursor-pointer transition-all hover:shadow-md ${
                    isSelected ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200'
                  }`}
                >
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${project.color}20` }}
                      >
                        <Folder
                          className="w-5 h-5"
                          style={{ color: project.color }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 truncate">
                          {project.name}
                        </h3>
                        <p className="text-sm text-gray-500 mt-0.5">
                          {project.description || '暂无描述'}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {project.startDate
                              ? new Date(project.startDate).toLocaleDateString('zh-CN')
                              : '未设置'}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full ${
                              project.status === 'active'
                                ? 'bg-green-100 text-green-700'
                                : project.status === 'completed'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {project.status === 'active'
                              ? '进行中'
                              : project.status === 'completed'
                              ? '已完成'
                              : '已归档'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* 进度条 */}
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                        <span>进度</span>
                        <span>{progress}%</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${progress}%`,
                            backgroundColor: project.color,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* 项目详情 */}
      <div className="lg:col-span-2">
        {selectedProject ? (
          <div className="space-y-4">
            {/* 项目头部 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${selectedProject.color}20` }}
                  >
                    <Folder
                      className="w-7 h-7"
                      style={{ color: selectedProject.color }}
                    />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      {selectedProject.name}
                    </h2>
                    <p className="text-gray-500 mt-1">
                      {selectedProject.description || '暂无描述'}
                    </p>
                    <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {selectedProject.startDate
                          ? new Date(selectedProject.startDate).toLocaleDateString('zh-CN')
                          : '未设置开始日期'}
                        {' - '}
                        {selectedProject.endDate
                          ? new Date(selectedProject.endDate).toLocaleDateString('zh-CN')
                          : '未设置结束日期'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openProjectModal(selectedProject.id)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => deleteProjectMutation.mutate(selectedProject.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* 里程碑 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Target className="w-5 h-5 text-purple-600" />
                  里程碑
                </h3>
                <button
                  onClick={() => {}}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                >
                  <Plus className="w-4 h-4" />
                  添加
                </button>
              </div>

              {milestones.length === 0 ? (
                <p className="text-gray-500 text-center py-4">暂无里程碑</p>
              ) : (
                <div className="space-y-2">
                  {milestones.map((milestone) => (
                    <div
                      key={milestone.id}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                    >
                      {getStatusIcon(milestone.status)}
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{milestone.name}</p>
                        {milestone.dueDate && (
                          <p className="text-sm text-gray-500">
                            截止日期: {new Date(milestone.dueDate).toLocaleDateString('zh-CN')}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 任务列表 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  任务
                </h3>
                <button
                  onClick={() => openTaskModal()}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                >
                  <Plus className="w-4 h-4" />
                  添加任务
                </button>
              </div>

              {tasks.length === 0 ? (
                <p className="text-gray-500 text-center py-4">暂无任务</p>
              ) : (
                <div className="space-y-2">
                  {tasks
                    .filter((t) => !t.parentId)
                    .map((task) => (
                      <div
                        key={task.id}
                        onClick={() => {
                          setSelectedTaskId(task.id);
                          openTaskModal(task.id);
                        }}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                      >
                        {getStatusIcon(task.status)}
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{task.title}</p>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                            {task.dueDate && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(task.dueDate).toLocaleDateString('zh-CN')}
                              </span>
                            )}
                            <span
                              className={`px-2 py-0.5 text-xs rounded-full ${
                                task.priority === 'high'
                                  ? 'bg-red-100 text-red-700'
                                  : task.priority === 'medium'
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-green-100 text-green-700'
                              }`}
                            >
                              {task.priority === 'high'
                                ? '高'
                                : task.priority === 'medium'
                                ? '中'
                                : '低'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full min-h-[400px] flex items-center justify-center">
            <div className="text-center">
              <Folder className="w-16 h-16 text-gray-200 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-500">选择一个项目</p>
              <p className="text-sm text-gray-400 mt-1">查看项目详情、里程碑和任务</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
